// @LTG-ID: F-040 (CHG-0104 · magazine index redesign)
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-040, CHG-0104 dispatch
// @CHANGED: 2026-05-29 session_007 (CHG-0104 · replaces 7-section anchor
//           accordion with a magazine-style flat index of all 22 published
//           articles; ADR-034 to be recorded in `10_DECISIONS.md`)
// @OWNER: web
//
// /journals index page — magazine tab layout. Sources 22 published articles
// from the CHG-0102 manifest + legacy Brief 01/02 adapter shims exposed
// through `data/journals/_index.js` → `ALL_PUBLISHED_ARTICLES`. Emits a
// site-wide Blog @type JSON-LD payload with 22 embedded BlogPosting
// entries so the homepage-level SEO carousel picks up automatically.
//
// Deprecated (not touched, left on disk for other consumers):
//   - `data/journals-index.js` (37-brief editorial pipeline)
//   - `components/journals/journals-index.css`
//   - `components/journals/JournalsSectionFigures.jsx`
//
// Feature-flag-free — pure replacement. Two legacy hand-JSX briefs remain
// discoverable through the same manifest thanks to the CHG-0104 adapter
// shim; their bespoke render pages (`pages/Brief01.jsx`, `pages/Brief02.jsx`)
// are untouched and continue to serve `/journals/what-is-my-business-worth`
// and `/journals/sde-vs-ebitda`.

import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ALL_PUBLISHED_ARTICLES } from "../data/journals/_index";
import "../components/journals/journals-magazine.css";

const CANONICAL = "https://letstalkgrowth.com/journals";
const OG_DEFAULT = "https://letstalkgrowth.com/journals/og/_default.jpg";
const DESCRIPTION =
    "Research-backed essays on business valuation, exit planning, and value creation for owner-operators.";
const PAGE_TITLE = "The Journal \u00b7 Let\u2019s Talk Growth";

// User-locked lead-essay pillar per CHG-0104 spec.
const FEATURED_SLUG = "business-value-drivers";

// Category taxonomy — matches BRIEF FRONTMATTER.articleSection exactly.
// CHG-0108: extended from 4 to 7 categories to accommodate 20 net-new articles.
// Personal Readiness (21-23), Industry-Specific (31-39), Series capstone (40).
const CATEGORY_ORDER = [
    "Valuation",
    "Exit Planning",
    "Value Drivers",
    "Deal Process",
    "Personal Readiness",
    "Industry-Specific",
    "Series capstone",
];

// -----------------------------------------------------------------------
// Blog @graph builder — one BlogPosting entry per article in the manifest.
// Consumed both at runtime via Helmet AND at build time via the CHG-0102
// postbuild `inject-meta.js` for the static /journals/index.html shell.
// -----------------------------------------------------------------------

function buildBlogJsonLd(articles) {
    return {
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": `${CANONICAL}#blog`,
        name: "Let\u2019s Talk Growth \u00b7 The Journal",
        description: DESCRIPTION,
        url: CANONICAL,
        publisher: {
            "@type": "Organization",
            name: "Let\u2019s Talk Growth",
            url: "https://letstalkgrowth.com",
            logo: {
                "@type": "ImageObject",
                url: "https://letstalkgrowth.com/logo/ltg-logo.png",
                width: 600,
                height: 60,
            },
        },
        blogPost: articles.map((a) => {
            const fm = a.FRONTMATTER;
            const ogAbs = fm.ogImage && fm.ogImage.startsWith("http")
                ? fm.ogImage
                : `https://letstalkgrowth.com${fm.ogImage || "/journals/og/_default.jpg"}`;
            return {
                "@type": "BlogPosting",
                "@id": `${fm.canonical}#post`,
                headline: fm.title,
                description: fm.deck,
                datePublished: fm.publishedISO,
                dateModified: fm.lastReviewed || fm.publishedISO,
                author: {
                    "@type": "Person",
                    name: fm.author,
                    jobTitle: fm.authorRole,
                },
                url: fm.canonical,
                image: ogAbs,
                keywords: fm.keywords || "",
                articleSection: fm.articleSection,
            };
        }),
    };
}

// -----------------------------------------------------------------------
// Small pure helpers.
// -----------------------------------------------------------------------

const MONTHS_CAPS = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

function fmtHumanDate(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${MONTHS_CAPS[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

// -----------------------------------------------------------------------
// Sub-components.
// -----------------------------------------------------------------------

const Masthead = () => (
    <section className="ltg-magj-masthead" data-testid="journals-masthead">
        <p className="ltg-magj-kicker">
            THE JOURNAL &middot; RESEARCH AND ESSAYS ON BUSINESS VALUATION,
            EXIT PLANNING, AND VALUE CREATION
        </p>
        <hr className="ltg-magj-rule" aria-hidden="true" />
    </section>
);

const LeadEssayHero = ({ brief }) => {
    if (!brief) return null;
    const { FRONTMATTER, SLUG } = brief;
    return (
        <Link
            to={`/journals/${SLUG}`}
            className="ltg-magj-hero"
            data-testid="journals-lead-essay"
        >
            <div className="ltg-magj-hero__media">
                <img
                    src={FRONTMATTER.ogImage}
                    alt={FRONTMATTER.title}
                    loading="eager"
                    width="1200"
                    height="630"
                />
            </div>
            <div className="ltg-magj-hero__body">
                <p className="ltg-magj-hero__kicker">
                    THE LEAD ESSAY &middot;{" "}
                    {FRONTMATTER.articleSection.toUpperCase()}
                </p>
                <h2 className="ltg-magj-hero__title">
                    {FRONTMATTER.title}
                </h2>
                {FRONTMATTER.deck ? (
                    <p className="ltg-magj-hero__deck">{FRONTMATTER.deck}</p>
                ) : null}
                <hr className="ltg-magj-hero__rule" aria-hidden="true" />
                <div className="ltg-magj-hero__byline">
                    <span
                        className="ltg-magj-avatar"
                        aria-hidden="true"
                    >
                        {FRONTMATTER.authorInitials || "SG"}
                    </span>
                    <span className="ltg-magj-hero__author">
                        {FRONTMATTER.author}
                    </span>
                    <span className="ltg-magj-hero__meta">
                        {FRONTMATTER.readMinutes} MIN
                    </span>
                </div>
            </div>
        </Link>
    );
};

const CategoryTabs = ({ counts, active, onSelect }) => {
    const items = [
        { key: "LATEST", label: "LATEST", count: counts.LATEST },
        ...CATEGORY_ORDER.map((c) => ({
            key: c,
            label: c.toUpperCase(),
            count: counts[c] || 0,
        })),
    ];
    return (
        <nav
            className="ltg-magj-tabs"
            role="tablist"
            aria-label="Filter journal articles by category"
            data-testid="journals-tabs"
        >
            {items.map((it) => {
                const testid = `journals-tab-${it.key.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                    <button
                        key={it.key}
                        type="button"
                        role="tab"
                        aria-selected={active === it.key}
                        className={`ltg-magj-tab ${
                            active === it.key ? "is-active" : ""
                        }`}
                        onClick={() => onSelect(it.key)}
                        data-testid={testid}
                    >
                        {it.label}{" "}
                        <span className="ltg-magj-tab__count">{it.count}</span>
                    </button>
                );
            })}
        </nav>
    );
};

const ArticleCard = ({ brief }) => {
    const { FRONTMATTER, SLUG } = brief;
    return (
        <Link
            to={`/journals/${SLUG}`}
            className="ltg-magj-card"
            data-testid={`journals-card-${SLUG}`}
        >
            {/* CHG-0110 Phase 4 · card thumbnail (Surface D). 800x600, lazy. */}
            <img
                src={`/journals-media/thumb/${SLUG}.jpg`}
                alt={FRONTMATTER.title}
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
                className="ltg-magj-card__thumb"
                data-testid={`journals-card-thumb-${SLUG}`}
            />
            <div className="ltg-magj-card__meta">
                <span className="ltg-magj-card__cat">
                    {FRONTMATTER.articleSection.toUpperCase()}
                </span>
                <span className="ltg-magj-card__date">
                    {fmtHumanDate(FRONTMATTER.publishedISO)}
                </span>
            </div>
            <h3 className="ltg-magj-card__title">{FRONTMATTER.title}</h3>
            {FRONTMATTER.deck ? (
                <p className="ltg-magj-card__deck">{FRONTMATTER.deck}</p>
            ) : null}
            <div className="ltg-magj-card__byline">
                <span className="ltg-magj-avatar" aria-hidden="true">
                    {FRONTMATTER.authorInitials || "SG"}
                </span>
                <span className="ltg-magj-card__author">
                    {FRONTMATTER.author || "Sara F. Gonzalez, CPA"}
                </span>
                <span className="ltg-magj-card__meta-right">
                    {FRONTMATTER.readMinutes} MIN
                </span>
            </div>
        </Link>
    );
};

const BottomBand = () => (
    <section className="ltg-magj-outro" data-testid="journals-outro">
        <p className="ltg-magj-outro__lead">More briefs in progress.</p>
        <p className="ltg-magj-outro__body">
            Subscribe to be first when the next essay lands.
        </p>
    </section>
);

// -----------------------------------------------------------------------
// Page component.
// -----------------------------------------------------------------------

export const Journals = () => {
    const [activeTab, setActiveTab] = useState("LATEST");

    useEffect(() => {
        document.title = PAGE_TITLE;
    }, []);

    const { featured, filtered, counts } = useMemo(() => {
        const feat = ALL_PUBLISHED_ARTICLES.find(
            (a) => a.SLUG === FEATURED_SLUG,
        );
        const c = { LATEST: ALL_PUBLISHED_ARTICLES.length };
        for (const cat of CATEGORY_ORDER) c[cat] = 0;
        for (const a of ALL_PUBLISHED_ARTICLES) {
            const s = a.FRONTMATTER.articleSection;
            if (c[s] !== undefined) c[s] += 1;
        }
        // CHG-0112 B2 + B5 · Only the LATEST tab exposes the featured
        // article as a separate <LeadEssayHero> — on that tab the grid
        // omits it (to avoid duplication). On every other tab the hero
        // is HIDDEN (see render below) and the featured article renders
        // inline as a normal card, so the badge count and rendered card
        // count align on every tab.
        //   LATEST tab: badge = ALL.length, grid = ALL.length - 1 cards + 1 hero
        //   any category tab: badge = section.length, grid = section.length cards, no hero
        const rest = ALL_PUBLISHED_ARTICLES.filter(
            (a) =>
                !(activeTab === "LATEST" && a.SLUG === FEATURED_SLUG),
        )
            .filter(
                (a) =>
                    activeTab === "LATEST" ||
                    a.FRONTMATTER.articleSection === activeTab,
            )
            .slice()
            .sort((a, b) =>
                b.FRONTMATTER.publishedISO.localeCompare(
                    a.FRONTMATTER.publishedISO,
                ),
            );
        return { featured: feat, filtered: rest, counts: c };
    }, [activeTab]);

    const blogJsonLd = useMemo(
        () => buildBlogJsonLd(ALL_PUBLISHED_ARTICLES),
        [],
    );

    return (
        <div className="ltg-magj" data-testid="journals-page">
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={DESCRIPTION} />
                <link rel="canonical" href={CANONICAL} />
                <meta property="og:type" content="website" />
                <meta
                    property="og:site_name"
                    content="Let&#39;s Talk Growth &middot; The Journal"
                />
                <meta property="og:title" content={PAGE_TITLE} />
                <meta property="og:description" content={DESCRIPTION} />
                <meta property="og:url" content={CANONICAL} />
                <meta property="og:image" content={OG_DEFAULT} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={PAGE_TITLE} />
                <meta name="twitter:description" content={DESCRIPTION} />
                <meta name="twitter:image" content={OG_DEFAULT} />
                <script
                    type="application/ld+json"
                    data-testid="journals-blog-jsonld"
                >
                    {JSON.stringify(blogJsonLd)}
                </script>
            </Helmet>
            <Masthead />
            {activeTab === "LATEST" ? (
                <LeadEssayHero brief={featured} />
            ) : null}
            <CategoryTabs
                counts={counts}
                active={activeTab}
                onSelect={setActiveTab}
            />
            <section
                className="ltg-magj-grid"
                data-testid="journals-grid"
                aria-live="polite"
            >
                {filtered.map((a) => (
                    <ArticleCard key={a.SLUG} brief={a} />
                ))}
            </section>
            <BottomBand />
        </div>
    );
};

export default Journals;
